import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { ArrowLeft, Download, FileText, Printer, Users, Calendar, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function DiscountsReport() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const printRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);
  
  // Estado para filtro de clientes
  const [selectedClientes, setSelectedClientes] = useState([]);
  
  // Estado para filtro de fechas
  const [fechaDesde, setFechaDesde] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Obtener informe de descuentos
      const res = await api.get("/clients/descuentos/informe");
      setData(res.data);
      
      // Seleccionar todos los clientes por defecto
      if (res.data?.clientes_con_descuentos) {
        setSelectedClientes(res.data.clientes_con_descuentos.map(c => c.cliente_id));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al cargar el informe de descuentos");
    } finally {
      setLoading(false);
    }
  };
  
  const handleClienteToggle = (clienteId) => {
    setSelectedClientes(prev => {
      const newSelection = prev.includes(clienteId)
        ? prev.filter(id => id !== clienteId)
        : [...prev, clienteId];
      
      // Asegurar que al menos un cliente esté seleccionado
      if (newSelection.length === 0) {
        toast.error("Debes seleccionar al menos un cliente");
        return prev;
      }
      
      return newSelection;
    });
  };
  
  const handleSelectAllClientes = () => {
    if (data?.clientes_con_descuentos) {
      setSelectedClientes(data.clientes_con_descuentos.map(c => c.cliente_id));
    }
  };
  
  const handleClearFechas = () => {
    setFechaDesde(null);
    setFechaHasta(null);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("es-ES");
    } catch {
      return dateStr;
    }
  };
  
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return "-";
    return Number(num).toLocaleString("es-ES", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };
  
  // Filtrar datos según clientes seleccionados y rango de fechas
  const getFilteredData = () => {
    if (!data?.clientes_con_descuentos) return [];
    
    return data.clientes_con_descuentos
      .filter(cliente => selectedClientes.includes(cliente.cliente_id))
      .map(cliente => {
        // Filtrar historial por rango de fechas
        let filteredHistorial = cliente.historial;
        
        if (fechaDesde || fechaHasta) {
          filteredHistorial = cliente.historial.filter(registro => {
            const fechaRegistro = new Date(registro.fecha);
            
            if (fechaDesde && fechaRegistro < fechaDesde) return false;
            if (fechaHasta) {
              // Incluir todo el día de fechaHasta
              const fechaHastaFin = new Date(fechaHasta);
              fechaHastaFin.setHours(23, 59, 59, 999);
              if (fechaRegistro > fechaHastaFin) return false;
            }
            
            return true;
          });
        }
        
        if (filteredHistorial.length === 0) return null;
        
        // Recalcular total acumulado solo del período filtrado
        const totalFiltrado = filteredHistorial.reduce(
          (sum, reg) => sum + (reg.cantidad_descontada || 0), 0
        );
        
        return {
          ...cliente,
          historial: filteredHistorial,
          total_acumulado: totalFiltrado
        };
      }).filter(Boolean);
  };
  
  const filteredClientes = getFilteredData();
  
  // Obtener nombres de clientes seleccionados para el PDF
  const getSelectedClientesNames = () => {
    if (!data?.clientes_con_descuentos) return "";
    return data.clientes_con_descuentos
      .filter(c => selectedClientes.includes(c.cliente_id))
      .map(c => c.cliente_nombre)
      .join(", ");
  };
  
  const handleGeneratePDF = async () => {
    if (selectedClientes.length === 0) {
      toast.error("Selecciona al menos un cliente");
      return;
    }
    
    setGenerating(true);
    try {
      const element = printRef.current;
      
      // Nombre del archivo con información del filtro
      let filename = "Informe_Descuentos";
      if (selectedClientes.length === 1 && data?.clientes_con_descuentos) {
        const cliente = data.clientes_con_descuentos.find(c => c.cliente_id === selectedClientes[0]);
        if (cliente) {
          filename += `_${cliente.cliente_nombre.replace(/[^a-zA-Z0-9]/g, "_")}`;
        }
      } else {
        filename += `_${selectedClientes.length}_clientes`;
      }
      
      if (fechaDesde || fechaHasta) {
        filename += `_${fechaDesde ? format(fechaDesde, "yyyyMMdd") : "inicio"}`;
        filename += `-${fechaHasta ? format(fechaHasta, "yyyyMMdd") : "fin"}`;
      }
      
      filename += `_${new Date().toISOString().slice(0, 10)}.pdf`;
      
      const opt = {
        margin: [10, 10, 10, 10],
        filename: filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: 'avoid-all' }
      };
      await html2pdf().set(opt).from(element).save();
      toast.success("Informe generado correctamente");
    } catch (error) {
      toast.error("Error al generar el PDF");
    } finally {
      setGenerating(false);
    }
  };
  
  const handlePrint = () => {
    if (selectedClientes.length === 0) {
      toast.error("Selecciona al menos un cliente");
      return;
    }
    window.print();
  };
  
  if (!hasRole("admin") && !hasRole("admin_consultor")) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">No tienes permisos para acceder a esta sección</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Cargando informe...</p>
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 no-print">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/reports")}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold text-slate-800">Informe de Descuentos</h1>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint} disabled={selectedClientes.length === 0}>
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
          <Button 
            onClick={handleGeneratePDF} 
            disabled={generating || selectedClientes.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Download className="w-4 h-4 mr-2" />
            {generating ? "Generando..." : "Descargar PDF"}
          </Button>
        </div>
      </div>
      
      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 no-print">
        {/* Filtro de Clientes */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" />
              Filtrar por Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="py-3">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs text-slate-500">
                {selectedClientes.length} de {data?.clientes_con_descuentos?.length || 0} seleccionados
              </span>
              <Button 
                variant="link" 
                size="sm" 
                onClick={handleSelectAllClientes}
                className="text-xs h-auto p-0"
              >
                Seleccionar todos
              </Button>
            </div>
            
            <div className="max-h-48 overflow-y-auto space-y-2 border rounded-md p-2">
              {data?.clientes_con_descuentos?.map((cliente) => (
                <div key={cliente.cliente_id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`cliente-${cliente.cliente_id}`}
                    checked={selectedClientes.includes(cliente.cliente_id)}
                    onCheckedChange={() => handleClienteToggle(cliente.cliente_id)}
                    data-testid={`checkbox-cliente-${cliente.cliente_id}`}
                  />
                  <Label 
                    htmlFor={`cliente-${cliente.cliente_id}`}
                    className="cursor-pointer text-sm flex-1 truncate"
                  >
                    {cliente.cliente_nombre}
                    <span className="text-xs text-slate-400 ml-2">
                      ({formatNumber(cliente.total_acumulado)} €)
                    </span>
                  </Label>
                </div>
              ))}
              
              {(!data?.clientes_con_descuentos || data.clientes_con_descuentos.length === 0) && (
                <p className="text-sm text-slate-400 text-center py-4">
                  No hay clientes con descuentos
                </p>
              )}
            </div>
            
            {selectedClientes.length === 0 && (
              <p className="text-red-500 text-xs mt-2">
                ⚠️ Debes seleccionar al menos un cliente
              </p>
            )}
          </CardContent>
        </Card>
        
        {/* Filtro de Fechas */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Filtrar por Fecha
            </CardTitle>
          </CardHeader>
          <CardContent className="py-3">
            <div className="flex gap-4 items-end">
              {/* Fecha Desde */}
              <div className="flex-1">
                <Label className="text-xs text-slate-500 mb-1 block">Desde</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="fecha-desde-button"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {fechaDesde ? format(fechaDesde, "dd/MM/yyyy", { locale: es }) : "Seleccionar"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={fechaDesde}
                      onSelect={setFechaDesde}
                      initialFocus
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Fecha Hasta */}
              <div className="flex-1">
                <Label className="text-xs text-slate-500 mb-1 block">Hasta</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="fecha-hasta-button"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {fechaHasta ? format(fechaHasta, "dd/MM/yyyy", { locale: es }) : "Seleccionar"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={fechaHasta}
                      onSelect={setFechaHasta}
                      initialFocus
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Botón limpiar */}
              {(fechaDesde || fechaHasta) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleClearFechas}
                  className="text-slate-500"
                >
                  Limpiar
                </Button>
              )}
            </div>
            
            {(fechaDesde || fechaHasta) && (
              <p className="text-xs text-blue-600 mt-3">
                <Filter className="w-3 h-3 inline mr-1" />
                Mostrando descuentos 
                {fechaDesde && ` desde ${format(fechaDesde, "dd/MM/yyyy", { locale: es })}`}
                {fechaHasta && ` hasta ${format(fechaHasta, "dd/MM/yyyy", { locale: es })}`}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Contenido del informe */}
      <div ref={printRef} className="bg-white p-6 shadow-lg border rounded-lg print-content">
        {/* Header con logo y datos empresa */}
        <div className="flex justify-between items-start mb-6 border-b-2 border-emerald-600 pb-4">
          <div className="flex items-center gap-4">
            {data?.empresa?.logo_url && (
              <img 
                src={`${window.location.origin}${data.empresa.logo_url}`} 
                alt="Logo" 
                className="h-16 object-contain"
                crossOrigin="anonymous"
              />
            )}
            <div>
              <h1 className="text-xl font-bold text-emerald-800">INFORME DE DESCUENTOS</h1>
              <p className="text-sm text-slate-600">Historial de descuentos aplicados a clientes</p>
            </div>
          </div>
          <div className="text-right text-sm">
            <p className="font-bold text-slate-800">{data?.empresa?.nombre || "EMPRESA"}</p>
            <p className="text-slate-600">CIF: {data?.empresa?.cif || ""}</p>
            <p className="text-slate-600">{data?.empresa?.direccion || ""}</p>
          </div>
        </div>
        
        {/* Resumen */}
        <div className="mb-6 bg-slate-50 p-4 rounded-lg">
          <p className="text-sm text-slate-600">
            Clientes en este informe: <span className="font-bold text-emerald-700">{filteredClientes.length}</span>
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Fecha de generación: {new Date().toLocaleDateString("es-ES")} {new Date().toLocaleTimeString("es-ES")}
          </p>
          {(fechaDesde || fechaHasta) && (
            <p className="text-xs text-blue-600 mt-1">
              Período: {fechaDesde ? format(fechaDesde, "dd/MM/yyyy", { locale: es }) : "Inicio"} 
              {" - "} 
              {fechaHasta ? format(fechaHasta, "dd/MM/yyyy", { locale: es }) : "Actualidad"}
            </p>
          )}
        </div>
        
        {/* Tabla por cliente */}
        {filteredClientes.length > 0 ? (
          filteredClientes.map((cliente) => (
            <div key={cliente.cliente_id} className="mb-6">
              <div className="bg-blue-50 p-3 rounded-t-lg border-b-2 border-blue-200">
                <h3 className="font-bold text-blue-800">{cliente.cliente_nombre}</h3>
                <p className="text-xs text-slate-600">CIF: {cliente.cliente_cif || "-"}</p>
                <p className="text-sm font-semibold text-emerald-700 mt-1">
                  Total {(fechaDesde || fechaHasta) ? "en período" : "acumulado"}: {formatNumber(cliente.total_acumulado)} €
                </p>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">Nº Factura</TableHead>
                    <TableHead className="w-28">Fecha</TableHead>
                    <TableHead className="text-right w-32">Descuento</TableHead>
                    <TableHead className="text-right w-32">Acumulado</TableHead>
                    <TableHead>Empresa</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cliente.historial.map((registro, regIdx) => (
                    <TableRow key={regIdx}>
                      <TableCell className="font-mono">{registro.factura_numero}</TableCell>
                      <TableCell>{formatDate(registro.fecha)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        -{formatNumber(registro.cantidad_descontada)} €
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {formatNumber(registro.acumulado)} €
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{registro.empresa_nombre}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {selectedClientes.length === 0 
                ? "Selecciona al menos un cliente para ver los descuentos"
                : "No hay descuentos registrados para los filtros seleccionados"}
            </p>
          </div>
        )}
        
        {/* Footer */}
        <div className="border-t border-slate-300 pt-4 mt-6 text-xs text-slate-500 text-right">
          <p>Fecha de impresión: {new Date().toLocaleDateString("es-ES")}</p>
        </div>
      </div>
      
      {/* Estilos de impresión */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-content { 
            box-shadow: none !important; 
            border: none !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `}</style>
    </div>
  );
}
